import type { RouteById, SuccessOf, SyncStreamName } from "@volgo/core/contract";
import { type CSSProperties, useEffect, useState } from "react";
import { downloadProgress } from "../../downloads/progress";
import { useGalleryInfo } from "../../downloads/useGalleryInfo";
import { useSession } from "../../session";
import { Button } from "../../ui/Button";
import { describeError } from "../../ui/errors";
import { formatCount, formatDateTime } from "../../ui/format";
import { STREAM_LABELS } from "../../ui/labels";
import { Loading } from "../../ui/States";
import { BotDrawer, BotSection, type BotStatus } from "./OpsBot";
import {
  BANDS,
  type DownloadFilter,
  DownloadsDrawer,
  DownloadTable,
  dueOf,
  type Job,
  type Overview,
  processed,
  RefreshDrawer,
  type Rule,
  resultLabel,
  StreamDrawer,
  type SyncStatus,
  streamState,
} from "./OpsDrawers";
import { RuleDrawer, RuleSection } from "./OpsRules";

interface Downloads {
  overview: SuccessOf<RouteById<"admin.downloadOverview">>;
  running: Job[];
  recent: Job[];
}
type Drawer =
  | { kind: "stream"; name: SyncStreamName }
  | { kind: "refresh" }
  | { kind: "downloads"; filter: DownloadFilter }
  /** null: a new rule */
  | { kind: "rule"; id: string | null }
  | { kind: "bot" }
  | null;

const time = (iso: string) => formatDateTime(iso).slice(-5);

/**
 * The ops tab (Spec M-1, design/bot/admin-final.html): a flow diagram on top, what needs a hand and the
 * downloads on the left, collection on the right; a click opens the matching drawer. Everything the former
 * sync and all-downloads screens showed is in it. Refreshed every 30 seconds while the tab is visible.
 */
export function OpsPage() {
  const { client } = useSession();
  const [overview, setOverview] = useState<Overview | null>(null);
  const [status, setStatus] = useState<SyncStatus | null>(null);
  const [countsError, setCountsError] = useState<unknown>(null);
  const [downloads, setDownloads] = useState<Downloads | null>(null);
  const [downloadsError, setDownloadsError] = useState<unknown>(null);
  const [rules, setRules] = useState<Rule[] | null>(null);
  const [rulesError, setRulesError] = useState<unknown>(null);
  const [bot, setBot] = useState<BotStatus | null>(null);
  const [botError, setBotError] = useState<unknown>(null);
  const [catalogTotal, setCatalogTotal] = useState<number | null>(null);
  const [round, setRound] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [resuming, setResuming] = useState<SyncStreamName | null>(null);
  const [drawer, setDrawer] = useState<Drawer>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: round requests a fresh snapshot and cancels an older response.
  useEffect(() => {
    let alive = true;
    let busy = false;
    let counting = false;
    function fetchInto<T>(call: Promise<T>, set: (value: T) => void, setError: (error: unknown) => void) {
      call.then(
        (value) => {
          if (!alive) return;
          set(value);
          setError(null);
        },
        (error: unknown) => {
          if (alive) setError(error);
        },
      );
    }
    function refreshDownloads() {
      fetchInto(
        Promise.all([
          client.call("admin.downloadOverview"),
          client.call("admin.listDownloads", { query: { status: "running", limit: 9 } }),
          client.call("admin.listDownloads", { query: { limit: 6 } }),
        ]),
        ([overview, running, recent]) =>
          setDownloads({ overview, running: running.items, recent: recent.items }),
        setDownloadsError,
      );
      fetchInto(client.call("admin.listRules"), (value) => setRules(value.items), setRulesError);
      fetchInto(client.call("admin.botStatus"), setBot, setBotError);
      // the catalog node only: on failure it keeps the last total ("—" before the first)
      fetchInto(
        client.call("catalog.categoryCounts"),
        (value) => setCatalogTotal(value.items.reduce((sum, item) => sum + item.count, 0)),
        () => undefined,
      );
    }
    async function refresh() {
      if (busy) return;
      busy = true;
      setRefreshing(true);
      refreshDownloads();
      if (!counting) {
        counting = true;
        fetchInto(
          client.call("admin.syncStreams").finally(() => {
            counting = false;
          }),
          setOverview,
          setCountsError,
        );
      }
      try {
        const value = await client.call("admin.syncStatus");
        if (alive) {
          setStatus(value);
          setLoadError(null);
        }
      } catch (error) {
        if (alive) setLoadError(error);
      } finally {
        busy = false;
        if (alive) setRefreshing(false);
      }
    }
    void refresh();
    const whenVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    const timer = setInterval(whenVisible, 30_000);
    document.addEventListener("visibilitychange", whenVisible);
    return () => {
      alive = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", whenVisible);
    };
  }, [client, round]);

  async function resume(name: SyncStreamName) {
    setResuming(name);
    setActionError(null);
    try {
      await client.call("admin.resumeStream", { params: { name } });
      setRound((n) => n + 1);
    } catch (error) {
      setActionError(error);
    } finally {
      setResuming(null);
    }
  }

  const reload = () => setRound((n) => n + 1);
  /** a rule the drawer changed: shown at once, then the next snapshot brings its counts */
  function putRule(rule: Rule) {
    setRules((list) =>
      list?.some((r) => r.id === rule.id)
        ? list.map((r) => (r.id === rule.id ? rule : r))
        : [...(list ?? []), rule],
    );
    reload();
  }

  const open = drawer?.kind === "stream" ? status?.items.find((s) => s.name === drawer.name) : undefined;
  const openedRule =
    drawer?.kind === "rule" && drawer.id ? rules?.find((r) => r.id === drawer.id) : undefined;
  return (
    <section aria-label="운영" className="ops">
      <div className="page-head ops-head">
        <p className="note">화면을 보는 동안 30초마다 상태 갱신 · 집계는 최대 60초 지연</p>
        <Button onClick={() => setRound((n) => n + 1)} disabled={refreshing}>
          {refreshing ? "갱신 중…" : "새로고침"}
        </Button>
      </div>
      {(loadError !== null || actionError !== null) && (
        <p className="note note-warn" role="alert">
          {describeError(actionError ?? loadError)}
          {loadError !== null && status ? " · 마지막으로 받은 상태를 표시합니다." : ""}
        </p>
      )}
      {countsError !== null && (
        <p className="note note-warn" role="alert">
          집계를 불러오지 못했습니다. {describeError(countsError)}
        </p>
      )}
      {!status && refreshing && <Loading />}
      {status && (
        <Flow
          status={status}
          overview={overview}
          downloads={downloads}
          catalogTotal={catalogTotal}
          rules={rules}
          bot={bot}
          onOpen={setDrawer}
        />
      )}
      {/* only the collection parts need admin.syncStreams; downloads, rules and bot stay usable without it */}
      <div className="ops-board">
        <div className="ops-col">
          {status && <Alerts overview={status} resuming={resuming} onResume={(name) => void resume(name)} />}
          <DownloadSlots downloads={downloads} error={downloadsError} />
          <section className="ops-w" aria-label="최근 다운로드">
            <header>
              <h2>최근 다운로드</h2>
              <Button className="ops-r" onClick={() => setDrawer({ kind: "downloads", filter: {} })}>
                전체 보기
              </Button>
            </header>
            {downloads && downloads.recent.length === 0 && <p className="ops-in note">다운로드가 없습니다</p>}
            {downloads && <DownloadTable jobs={downloads.recent} guilds={bot?.guilds} compact />}
          </section>
        </div>
        <div className="ops-col">
          {status && <Collection status={status} overview={overview} onOpen={setDrawer} />}
          <RuleSection rules={rules} error={rulesError} onOpen={(id) => setDrawer({ kind: "rule", id })} />
          <BotSection bot={bot} error={botError} onOpen={() => setDrawer({ kind: "bot" })} />
        </div>
      </div>
      {open && status && (
        <StreamDrawer
          key={open.name}
          stream={open}
          overview={status}
          round={round}
          resuming={resuming !== null}
          onResume={() => void resume(open.name)}
          onClose={() => setDrawer(null)}
        />
      )}
      {drawer?.kind === "refresh" && overview && (
        <RefreshDrawer overview={overview} onClose={() => setDrawer(null)} />
      )}
      {drawer?.kind === "downloads" && (
        <DownloadsDrawer
          initial={drawer.filter}
          rules={rules ?? []}
          guilds={bot?.guilds ?? []}
          onClose={() => setDrawer(null)}
        />
      )}
      {drawer?.kind === "rule" && (drawer.id === null || openedRule) && (
        <RuleDrawer
          rule={openedRule ?? null}
          onSaved={(rule) => {
            putRule(rule);
            setDrawer({ kind: "rule", id: rule.id });
          }}
          onChanged={reload}
          onDeleted={(id) => {
            setRules((list) => (list ?? []).filter((r) => r.id !== id));
            setDrawer(null);
            reload();
          }}
          onShowDownloads={(ruleId) => setDrawer({ kind: "downloads", filter: { origin: "rule", ruleId } })}
          onClose={() => setDrawer(null)}
        />
      )}
      {drawer?.kind === "bot" && (
        <BotDrawer
          bot={bot}
          error={botError}
          onBot={setBot}
          onChanged={reload}
          onClose={() => setDrawer(null)}
        />
      )}
    </section>
  );
}

/**
 * The way a gallery goes, left to right: found (①), filled in (②), in the catalog, queued (③), downloaded
 * (④), readable (⑤); below the queue, where its entries come from (web, bot, rules). Every box opens its drawer.
 */
function Flow({
  status,
  overview,
  downloads,
  catalogTotal,
  rules,
  bot,
  onOpen,
}: {
  status: SyncStatus;
  overview: Overview | null;
  downloads: Downloads | null;
  catalogTotal: number | null;
  rules: readonly Rule[] | null;
  bot: BotStatus | null;
  onOpen: (drawer: Drawer) => void;
}) {
  const head = status.items.find((s) => s.name === "ex-head");
  const meta = status.items.find((s) => s.name === "meta");
  const due = overview ? formatCount(dueOf(overview)) : "집계 중";
  const targets = overview
    ? formatCount(overview.refreshBands.reduce((sum, band) => sum + band.targetCount, 0))
    : "집계 중";
  const d = downloads?.overview;
  const failedGuilds = bot?.guilds.filter((g) => g.commands.state === "failed").length ?? 0;
  const firstRule = rules?.find((r) => r.enabled) ?? rules?.[0];
  return (
    <nav className="ops-flow" aria-label="흐름도">
      {head && (
        <FlowNode
          node="ex-head"
          tone={streamState(head, status.checkedAt).tone}
          label="① 새 목록"
          value={head.latestRun ? `+${formatCount(head.latestRun.newGids)}` : "—"}
          sub={
            head.latestRun
              ? `${time(head.latestRun.startedAt)} · ${resultLabel(head.latestRun)}`
              : "실행 기록 없음"
          }
          onClick={() => onOpen({ kind: "stream", name: "ex-head" })}
        />
      )}
      {meta && (
        <FlowNode
          node="meta"
          tone={streamState(meta, status.checkedAt).tone}
          label="② 정보 채우기"
          value={
            meta.pausedAt ? "멈춤" : overview ? `대기 ${formatCount(overview.pendingMetaCount)}` : "집계 중"
          }
          sub={meta.pausedAt ? streamState(meta, status.checkedAt).text : `기한 지남 ${due}`}
          onClick={() => onOpen({ kind: "stream", name: "meta" })}
        />
      )}
      <FlowNode
        node="refresh"
        tone="idle"
        label="카탈로그"
        value={catalogTotal === null ? "—" : formatCount(catalogTotal)}
        sub={`다시 확인 대상 ${targets}`}
        disabled={!overview}
        onClick={() => onOpen({ kind: "refresh" })}
      />
      <FlowNode
        node="queue"
        tone="idle"
        label="③ 대기열"
        value={d ? formatCount(d.queued.total) : "—"}
        sub={
          d
            ? `읽기 ${formatCount(d.queued.byPriority.read)} · 검색 ${formatCount(d.queued.byPriority.search)} · 규칙 ${formatCount(d.queued.byPriority.rule)}`
            : "불러오는 중"
        }
        onClick={() => onOpen({ kind: "downloads", filter: { status: "queued" } })}
      />
      <FlowNode
        node="downloads"
        tone={d && d.slots.running > 0 ? "run" : "idle"}
        label="④ 다운로드"
        value={d ? `${d.slots.running} / ${d.slots.limit}` : "—"}
        sub={d ? `Archive 슬롯 · 실패 ${formatCount(d.last24h.failed)}/24시간` : "Archive 슬롯"}
        onClick={() => onOpen({ kind: "downloads", filter: {} })}
      />
      <FlowNode
        node="done"
        tone="idle"
        label="⑤ 읽을 수 있음"
        value={d ? `+${formatCount(d.last24h.done)}` : "—"}
        sub="최근 24시간"
        onClick={() => onOpen({ kind: "downloads", filter: { status: "done" } })}
        last
      />
      <fieldset className="ops-inputs">
        <legend className="ops-inputs-h">대기열로 들어오는 곳</legend>
        <button
          type="button"
          data-entry="web"
          onClick={() => onOpen({ kind: "downloads", filter: { origin: "web" } })}
        >
          <span className="ops-org" data-origin="web">
            웹
          </span>
          <b>{d ? formatCount(d.last24h.byOrigin.web) : "—"}</b>
          <span className="note">/24시간 완료</span>
        </button>
        <button type="button" data-entry="bot" onClick={() => onOpen({ kind: "bot" })}>
          <span className="ops-org" data-origin="bot_read">
            봇
          </span>
          <b>{bot ? (bot.configured ? `서버 ${bot.guilds.length}` : "설정 없음") : "—"}</b>
          {failedGuilds > 0 && <span className="ops-dot ops-r" data-tone="warn" title="명령 등록 실패" />}
        </button>
        <button
          type="button"
          data-entry="rule"
          onClick={() => onOpen({ kind: "rule", id: firstRule?.id ?? null })}
        >
          <span className="ops-org" data-origin="rule">
            규칙
          </span>
          <b>{rules ? `${rules.filter((r) => r.enabled).length}개 켜짐` : "—"}</b>
        </button>
      </fieldset>
    </nav>
  );
}

function FlowNode({
  node,
  tone,
  label,
  value,
  sub,
  onClick,
  last = false,
  disabled = false,
}: {
  node: string;
  tone: "bad" | "warn" | "run" | "idle";
  label: string;
  value: string;
  sub: string;
  onClick: () => void;
  last?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className="ops-node"
      data-node={node}
      data-tone={tone}
      data-last={last}
      onClick={onClick}
      disabled={disabled}
    >
      <span className="ops-node-lb">
        <span className="ops-dot" data-tone={tone} />
        {label}
      </span>
      <span className="ops-node-v">{value}</span>
      <span className="ops-node-s">{sub}</span>
    </button>
  );
}

function Alerts({
  overview,
  resuming,
  onResume,
}: {
  overview: SyncStatus;
  resuming: SyncStreamName | null;
  onResume: (name: SyncStreamName) => void;
}) {
  const paused = overview.items.filter((s) => s.pausedAt);
  if (paused.length === 0)
    return (
      <p className="ops-ok">
        <span className="ops-dot" data-tone="ok" />
        지금 손볼 것이 없습니다 · 상태 확인 {time(overview.checkedAt)} · 30초마다 갱신
      </p>
    );
  return (
    <ul className="ops-alerts" aria-label="주의">
      {paused.map((s) => (
        <li key={s.name}>
          <span className="ops-dot" data-tone="bad" />
          <div>
            <b>{STREAM_LABELS[s.name]}이 멈췄습니다</b>
            <p>
              {formatDateTime(s.pausedAt as string)}부터 · {streamState(s, overview.checkedAt).text}
            </p>
          </div>
          <Button
            aria-label={`${STREAM_LABELS[s.name]} 일시정지 해제`}
            disabled={resuming !== null}
            onClick={() => onResume(s.name)}
          >
            재개
          </Button>
        </li>
      ))}
    </ul>
  );
}

function DownloadSlots({ downloads, error }: { downloads: Downloads | null; error: unknown }) {
  const running = downloads?.running ?? [];
  const infoOf = useGalleryInfo(running);
  const d = downloads?.overview;
  const limit = d?.slots.limit ?? 9;
  return (
    <section className="ops-w" aria-label="다운로드">
      <header>
        <h2>다운로드</h2>
        {d && (
          <span className="ops-r note">
            {d.slots.running}/{limit} 사용 중 · 30초마다
          </span>
        )}
      </header>
      {error !== null && (
        <p className="ops-in note note-warn" role="alert">
          {describeError(error)}
        </p>
      )}
      <ol className="ops-slots" aria-label="Archive 슬롯">
        {running.map((job) => {
          const progress = downloadProgress(job);
          const title = infoOf(job).title ?? `갤러리 ${job.gid}`;
          return (
            <li
              key={job.id}
              data-gid={job.gid}
              title={title}
              style={
                {
                  "--fill": `${progress.total ? (progress.done / progress.total) * 100 : 0}%`,
                } as CSSProperties
              }
            >
              <b>{title}</b>
              <span>
                {job.runner === "ec2" ? "EC2 · " : ""}
                {progress.text}
              </span>
            </li>
          );
        })}
        {/* shortcut: an EC2 job waiting for disk holds a slot but shows as empty; list queued EC2 jobs if that confuses */}
        {Array.from({ length: Math.max(0, limit - running.length) }, (_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: empty slots have nothing but their place
          <li key={i} data-empty="true">
            <span>빈 슬롯</span>
          </li>
        ))}
      </ol>
      {d && (
        <dl className="ops-row3">
          <div>
            <dt>대기</dt>
            <dd>{formatCount(d.queued.total)}</dd>
            <dd className="note">
              읽기 {formatCount(d.queued.byPriority.read)} · 검색 {formatCount(d.queued.byPriority.search)} ·
              규칙 {formatCount(d.queued.byPriority.rule)}
            </dd>
          </div>
          <div>
            <dt>24시간 완료</dt>
            <dd>{formatCount(d.last24h.done)}</dd>
            <dd className="note">
              웹 {formatCount(d.last24h.byOrigin.web)} · 봇 읽기 {formatCount(d.last24h.byOrigin.botRead)} ·
              봇 검색 {formatCount(d.last24h.byOrigin.botSearch)} · 규칙{" "}
              {formatCount(d.last24h.byOrigin.rule)}
            </dd>
          </div>
          <div>
            <dt>24시간 실패</dt>
            <dd data-bad={d.last24h.failed > 0}>{formatCount(d.last24h.failed)}</dd>
          </div>
        </dl>
      )}
    </section>
  );
}

function Collection({
  status,
  overview,
  onOpen,
}: {
  status: SyncStatus;
  overview: Overview | null;
  onOpen: (drawer: Drawer) => void;
}) {
  const most = Math.max(1, ...(overview?.refreshBands ?? []).map((b) => b.targetCount));
  return (
    <section className="ops-w" aria-label="수집">
      <header>
        <h2>수집</h2>
        <span className="ops-r note">
          {overview
            ? `정보 대기 ${formatCount(overview.pendingMetaCount)} · 기한 지남 ${formatCount(dueOf(overview))}`
            : "건수 집계 중…"}
        </span>
      </header>
      <ul className="ops-list">
        {status.items.map((s) => {
          const state = streamState(s, status.checkedAt);
          return (
            <li key={s.name}>
              <button
                type="button"
                data-stream={s.name}
                onClick={() => onOpen({ kind: "stream", name: s.name })}
              >
                <span className="ops-dot" data-tone={state.tone} />
                <span>
                  {STREAM_LABELS[s.name]}
                  <small>
                    {s.latestRun
                      ? `${time(s.latestRun.startedAt)} · ${processed(s.latestRun)}`
                      : "실행 기록 없음"}
                  </small>
                </span>
                <span className="ops-pill" data-tone={state.tone}>
                  {state.text}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <button
        type="button"
        className="ops-bands"
        disabled={!overview}
        onClick={() => onOpen({ kind: "refresh" })}
      >
        <span className="note">다시 확인(최근 60일)</span>
        {overview?.refreshBands.map((band) => (
          <span key={band.intervalHours} className="ops-band">
            <span>{BANDS[band.intervalHours]}</span>
            <span className="ops-bar">
              <i style={{ width: `${(band.targetCount / most) * 100}%` }} />
            </span>
            <span data-due={band.dueCount > 0}>
              {band.dueCount > 0 ? `지남 ${formatCount(band.dueCount)}` : formatCount(band.targetCount)}
            </span>
          </span>
        ))}
      </button>
    </section>
  );
}
